import {
  ChakraProvider,
  Box,
  Text,
  Grid,
  GridItem,
  VStack,
  Stack,
  HStack,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  extendTheme,
  Spinner,
} from '@chakra-ui/react'
import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react'
import Header from '../components/Header'
import {
  addDays,
  format,
  parse,
  subDays,
  intervalToDuration,
  parseISO,
  differenceInDays,
  startOfWeek,
  getDay,
} from 'date-fns'
import Sidebar from '../components/Sidebar'
import '../App.css'
import Api from '../api/api'
import AddNewReservation from '../components/AddNewReservation'
import { ChatIcon, EditIcon, PhoneIcon, QuestionIcon } from '@chakra-ui/icons'
import { MultiSelectTheme } from 'chakra-multiselect';
import AllReservations from '../components/AllReservations'
import PropTypes from 'prop-types'
import { ReservationCountContext } from '../context/ReservationCountProvider'

import {
  Calendar,
  Views,
  DateLocalizer,
  momentLocalizer,
  dateFnsLocalizer,
} from 'react-big-calendar'
import moment from 'moment'

function App() {
  const [date, setDate] = useState<string | null>(null)
  const [reservations, setReservations] = useState<Array<any> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dates, setDates] = useState<Array<any> | null>(null)
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timer | null>(null)
  const [reservationsCountInterval, setReservationsCountInterval] = useState<NodeJS.Timer | null>(
    null,
  )
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [reservationsCounter, setReservationsCounter] = useState<Array<any> | null>(null)
  const [locations, setLocations] = useState<Array<string>>([])
  const [reservationsLoading, setReservationsLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [includeRooms, setIncludeRooms] = useState(false)
  const [selectedDates, setSelectedDates] = useState<Date[] | string[]>([])
  const [editingReservation, setEditingReservation] = useState<any>(null)
  const [reservationsList, setReservationsList] = useState<any[]>([])
  const [reservationsCounterForDay, setReservationsCounterForDay] = useState<any>({})
  const [allReservations, setAllReservations] = useState([])

  useEffect(() => {
    getLocations()
    getAllReservations()
  }, [])

  useEffect(() => {
    setReservationsList([])
    mapReservations(allReservations)
  }, [allReservations, includeRooms])

  const getLocations = () => {
    Api.getLocations()
      .then((result) => {
        setLocations(result)
      })
      .catch(() => {
        setError('Error on fetching locations')
      })
  }

  const getAllReservations = () => {
      setReservationsLoading(true)
      Api.getCalendarReservations(selectedDates)
        .then((result) => {
          setAllReservations(result)
        })
        .catch(() => {
          setError('Error on fetching orders')
        })
  }

  const mapReservations = (reservations) => {
    if (reservations.length === 0) {
      return;
    }

    const tempReservationCountersForDay = {}

    const tempReservationList = [];
    reservations.forEach((reservation) => {
      if (reservation.type === 'roomReservation' && !includeRooms) {
        return
      }

      const date = moment(reservation.start).format('YYYY-MM-DD')
      if (tempReservationCountersForDay[date]) {
        tempReservationCountersForDay[date] += parseInt(reservation.guests || 0)
      } else {
        tempReservationCountersForDay[date] = parseInt(reservation.guests || 0)
      }

      tempReservationList.push({
        id: reservation.id,
        title: reservation.name,
        start: new Date(reservation.start),
        end: new Date(reservation.end),
        desc: reservation.comment,
        inquiry: reservation.inquiry,
        tasting: reservation.tasting,
        type: reservation.type,
        location: reservation.location,
        reservationObject: reservation,
      })
    })

    setReservationsCounterForDay(tempReservationCountersForDay)
    setReservationsList(tempReservationList)
    setReservationsLoading(false)
  }

  function EventAgenda({ event }) {
    return (
      <span>
        <span className="location">{event.location}</span>
        <em className='event-title'>{event.title}</em>
        <p className='event-comment'>{event.desc}</p>
      </span>
    )
  }
  EventAgenda.propTypes = {
    event: PropTypes.object,
  }

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (
      date: number | Date,
      options?:
        | {
            locale?: Locale | undefined
            weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined
          }
        | undefined,
    ) => startOfWeek(date, { ...options, weekStartsOn: 1 }),
    getDay,
    locales: {
      'lt': require('date-fns/locale/lt'),
      'en': require('date-fns/locale/lt'),
    },
  })
  const themeWithSelect = extendTheme({
    components: {
      MultiSelect: {
        ...MultiSelectTheme,
        baseStyle: (props: any) => {
          const baseStyle = MultiSelectTheme.baseStyle(props) as any
          return {
            ...baseStyle,
          }
        },
      },
    },
  })

  // eslint-disable-next-line react/prop-types
  const CustomDateHeader = ({ label, drilldownView, onDrillDown, date }) => {
    // @ts-ignore
    const { reservationsCounterForDay } = useContext(ReservationCountContext)
    const counterForDay = reservationsCounterForDay[moment(date).format('YYYY-MM-DD')]

    return (
      <>
        <span className='reservation-counter'>{counterForDay}</span>
        <a href='#' onClick={onDrillDown}>
          {label}
        </a>
      </>
    )
  }

  const { components, views } = useMemo(
    () => ({
      components: {
        agenda: {
          event: EventAgenda,
        },
        month: {
          dateHeader: CustomDateHeader,
        },
        week: {
          header: CustomDateHeader,
        },
      },
      views: Object.keys(Views).map((k) => Views[k]),
    }),
    [],
  )



  const handleSelectEvent = useCallback((event) => {
    if (event.type !== 'localReservation') {
      return;
    }
    setEditingReservation(event.reservationObject)
  }, [])

  const eventStyleGetter = (event, start, end, isSelected) => {
    let color = '';
    if (event.inquiry === 1) {
      color = 'inquiry-yellow-color'
    } else if (event.inquiry) {
      color = 'inquiry-yellow-color'
    } else if (event.tasting) {
      color = 'tasting-color'
    } else if (event.type === 'localReservation') {
      color = 'local-reservation-color'
    } else {
      color = 'default-green-color'
    }

    return {
      className: color,
    }
  }
  const reservationsCalendar = (props) => (
    <Box className='fullHeight' px={4} py={4}>
      <Calendar
        localizer={localizer}
        events={reservationsList}
        startAccessor='start'
        endAccessor='end'
        components={components}
        showMultiDayTimes
        step={60}
        views={views}
        popup
        eventPropGetter={eventStyleGetter}
        onSelectEvent={handleSelectEvent}
      />
    </Box>
  )

  const reservationsPage = () => {
    if (
      reservationsLoading ||
      !reservationsList || reservationsList.length === 0) {
      return (
        <Stack pl={{ base: 0, md: 48 }} w='full' h='90vh' align='center' justify='center'>
          <Spinner
            thickness='4px'
            speed='0.65s'
            emptyColor='gray.200'
            color='green.500'
            size='xl'
          />
        </Stack>
      )
    }

    return reservationsCalendar({})
  }

  const onReservationCreated = () => {
    getAllReservations()
  }


  const onAllReservationsChange = (value: any) => {
    const checked = value.target.checked
    setIncludeRooms(checked)
  }

  return (
    <ChakraProvider theme={themeWithSelect}>
      <Header
        lastUpdate={lastUpdate}
        selectedDates={selectedDates}
        allReservations={includeRooms}
        onAllReservations={onAllReservationsChange}
      />
      <ReservationCountContext.Provider
        value={{ reservationsCounterForDay, setReservationsCounterForDay }}
      >
        <Stack>{reservationsPage()}</Stack>
      </ReservationCountContext.Provider>
      <AddNewReservation
        date={date}
        reservationCreated={onReservationCreated}
        locations={locations}
        editingReservation={editingReservation}
        stopEditingReservation={() => setEditingReservation(null)}
      />
    </ChakraProvider>
  )
}

export default App

